import type { ApiResponseNormalizer, TableVariant } from "@/types/table";
import { normalizeEmployeeMonthMatrix, normalizeKpiByOffice } from "@/lib/tableNormalizers";
import { apiDownloadFile, apiFetchJson } from "@/services/apiClient";
import { buildQueryString } from "@/analytics/api";
import type { UtilisationFilters } from "@/analytics/types";
import { appendSortParams, DEFAULT_SORT, type SortState } from "@/lib/tableSort";
import { API_URL } from "@/lib/utils";

const drillThroughPath = "/drill-through";

export type UtilisationReportConfig = {
  id: string;
  title: string;
  endpoint: string;
  normalizer: ApiResponseNormalizer;
  variant: TableVariant;
  /** Whether this report's columns are clickable/sortable via sort_by/sort_dir. */
  sortable?: boolean;
};

export const utilisationReports: UtilisationReportConfig[] = [
  {
    id: "kpi-by-office",
    title: "KPI By Office",
    endpoint: `${drillThroughPath}/kpi-by-office`,
    normalizer: normalizeKpiByOffice,
    variant: "matrix",
  },
  {
    id: "kpi-by-employee-month",
    title: "Utilisation% per Month",
    endpoint: `${drillThroughPath}/kpi-by-employee-month`,
    normalizer: normalizeEmployeeMonthMatrix,
    variant: "matrix",
    sortable: true,
  },
  {
    id: "kpi-hours-by-employee-month",
    title: "Chargeable Hours per Month",
    endpoint: `${drillThroughPath}/kpi-hours-by-employee-month`,
    normalizer: normalizeEmployeeMonthMatrix,
    variant: "matrix",
    sortable: true,
  },
];

export function fetchUtilisationReport(
  endpoint: string,
  filters: UtilisationFilters,
  signal?: AbortSignal,
  sort?: SortState,
): Promise<unknown> {
  const base = buildReportUrl(endpoint, filters);
  const sortParams = new URLSearchParams();
  appendSortParams(sortParams, sort ?? DEFAULT_SORT);
  const sortQs = sortParams.toString();
  const url = sortQs ? `${base}${base.includes("?") ? "&" : "?"}${sortQs}` : base;
  return apiFetchJson(url, { method: "GET", signal });
}

export type UtilisationExportOption =
  | "kpi_by_office"
  | "employee_month_utilisation"
  | "employee_month_hours";

export function downloadUtilisationDrillThroughExcel(
  filters: UtilisationFilters,
  exportOptions: UtilisationExportOption[],
): Promise<void> {
  const params = new URLSearchParams();
  filters.legacy_location.forEach((value) => params.append("legacy_location", value));
  filters.employees.forEach((value) => params.append("employees", value));
  filters.emp_categories.forEach((value) => params.append("emp_categories", value));
  filters.service_lines.forEach((value) => params.append("service_lines", value));
  if (filters.date_from) params.append("date_from", filters.date_from);
  if (filters.date_to) params.append("date_to", filters.date_to);
  exportOptions.forEach((option) => params.append("export_options", option));

  return apiDownloadFile(
    `${API_URL}/utilisation${drillThroughPath}/export?${params.toString()}`,
    exportOptions.length > 1 ? "Utilisation Drill Through Exports.zip" : "utilisation-drill-through.xlsx",
  );
}

function buildReportUrl(endpoint: string, filters: UtilisationFilters): string {
  return `${API_URL}${endpoint}${buildQueryString(filters)}`;
}
